import { memo } from 'react';

interface PercentCellProps {
  value: number | null | undefined;
  decimals?: number;
  showArrow?: boolean;
  colorCoded?: boolean;
}

// Arrow icons
const ArrowUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 2L10 7H2L6 2Z" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 10L2 5H10L6 10Z" />
  </svg>
);

export const PercentCell = memo(({
  value,
  decimals = 2,
  showArrow = true,
  colorCoded = true
}: PercentCellProps) => {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const formattedValue = Math.abs(value).toLocaleString('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  const isPositive = value > 0;
  const isNegative = value < 0;
  const isZero = value === 0;

  const color = colorCoded
    ? isPositive
      ? 'var(--color-positive)'
      : isNegative
        ? 'var(--color-negative)'
        : 'var(--color-text-muted)'
    : 'var(--color-text-primary)';

  const bgColor = colorCoded
    ? isPositive
      ? 'var(--color-positive-bg)'
      : isNegative
        ? 'var(--color-negative-bg)'
        : 'transparent'
    : 'transparent';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 6px',
        borderRadius: 'var(--radius-sm)',
        background: bgColor,
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        color,
        fontSize: '12px',
        fontWeight: 500
      }}
    >
      {showArrow && !isZero && (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {isPositive ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </span>
      )}
      <span>
        {isNegative && '−'}
        {formattedValue}%
      </span>
    </span>
  );
});

PercentCell.displayName = 'PercentCell';
